import uuid from 'uuid/v4';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { Mapping, ReplaceAroundStep, ReplaceStep } from '@atlaskit/editor-prosemirror/transform';

import type { ProactiveAIBlock } from '../ai-spelling-grammar/states';
import {
	getIncludedProactiveAINodeTypes,
	getProactiveAIBlockNodeTypes,
	getSentencesFromNode,
	updateBlockPositions,
} from '../ai-spelling-grammar/utils';

import { type AISGBetterTrafficProjectionsPluginState } from './types';

/**
 * This is mostly copy of findProactiveAIBlockToUpdate in "ai-spelling-grammer" plugin.
 *  from "platform/packages/editor/editor-plugin-ai/src/pm-plugins/ai-spelling-grammar/utils.ts" file.
 * It's slightly modified: modifications are;
 * - removed use of isSpellingAndGrammarEnabled as we want to project traffic as if it's enabled
 *   all the time.
 * - removed finding inlineNodePositions as we won't be creating suggestions.
 *
 * If you make change in this function then update above list of modifications.
 */
export function findProactiveAIBlockToUpdate(
	tr: Transaction | ReadonlyTransaction,
	pluginState: AISGBetterTrafficProjectionsPluginState,
	newEditorState: EditorState,
) {
	const { proactiveAIBlocks, splitParagraphIntoSentences } = pluginState;
	const shouldIgnoreChanges =
		tr.getMeta('isRemote') ||
		tr.getMeta('replaceDocument') ||
		tr.getMeta('suggestionAccepted') ||
		tr.getMeta('isAiContentTransformation');

	const nodeTypes = getProactiveAIBlockNodeTypes(newEditorState);
	const includedNodeTypes = getIncludedProactiveAINodeTypes(newEditorState);

	/**
	 * Transaction is collection of different steps.
	 * Each step can update different part of the doc.
	 * So we need to go through each step to find block updated/added by each step.
	 * For that, we need to look at doc after apply step.
	 *
	 * That's why here we are creating list of docs created after applying each step.
	 * tr.docs contain list that has doc created before each step.
	 * tr.doc is final version of doc created after applying last step.
	 *
	 * We need doc created after applying step.
	 * So for doc after applying first step (step 0) will be trDocsWithFinalDoc[1].
	 */
	const trDocsWithFinalDoc = tr.docs.concat([tr.doc]);
	const replaceStepsWithDocs: Array<{ step: ReplaceStep | ReplaceAroundStep; doc: PMNode }> = [];
	tr.steps.forEach((step, index) => {
		const stepDoc = trDocsWithFinalDoc[index + 1];
		if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
			replaceStepsWithDocs.push({ step, doc: stepDoc });
		}
	});

	if (proactiveAIBlocks && replaceStepsWithDocs.length > 0) {
		let updatedProactiveAIBlock: Array<ProactiveAIBlock & { node?: PMNode; newBlock?: boolean }> =
			Array.from(proactiveAIBlocks);

		/**
		 * Iterate through each step and step doc to find blocks to replace or add new blocks.
		 */
		replaceStepsWithDocs.forEach(({ step, doc }) => {
			const newProactiveAIBlocks: Array<ProactiveAIBlock & { node?: PMNode; newBlock?: boolean }> =
				[];
			const mapping = new Mapping();
			const stepMap = step.getMap();
			mapping.appendMap(stepMap);

			/**
			 * First find new nodes that needs to be checked against
			 * proactive AI prompt.
			 */
			stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
				doc.nodesBetween(newStart, Math.min(newEnd, doc.content.size), (node, pos) => {
					if (
						nodeTypes.has(node.type) &&
						!newProactiveAIBlocks.find(
							(newProactiveAIBlock) =>
								/**
								 * When new table is created, tableCell nodes created for first row (non header one)
								 * is reused for other rows.
								 * So only checking "newProactiveAIBlock.node === node" condition is not enough.
								 * We need to reuse same node to create block (as it has been reused in all rows)
								 * 	if position is different.
								 */
								newProactiveAIBlock.node === node && newProactiveAIBlock.from === pos + 1,
						)
					) {
						const from = pos + 1,
							to = pos + node.nodeSize - 1;
						newProactiveAIBlocks.push({
							id: uuid(),
							from,
							to,
							text: node.textContent,
							nodeTypeName: node.type.name,

							needSpellingAndGrammarCheck: !shouldIgnoreChanges,
							newBlock: true,
							node,
							sentences: splitParagraphIntoSentences
								? getSentencesFromNode({
										node,
										paragraphStartPos: from,
										needSpellingAndGrammarCheck: true,
										paragraphIgnoredRanges: [],
									})
								: undefined,

							// dont' need for traffic projections
							ignoredRanges: [],
						});
						return false;
					}
					return includedNodeTypes.has(node.type);
				});
			});

			/**
			 * Now find existing range of proactiveAIBlock that needs to be
			 * replaced with new blocks found.
			 *
			 * ASSUMPTIONS:
			 * 1. We have assumed here that new proactiveAIBlocks will be in
			 *    continous range.
			 *    For example: New 4 blocks will be from positions 350 to 550.
			 *                 Let's say there are 3 blocks before 350 and
			 *                 2 blocks after 550. And there were 2 blocks in 350 to 550 before.
			 *                 That means existing Array is
			 *                 [b1, b2, b3, -- b4, b5 --, b6, b7 )], affected blocks are b4, b5.
			 *                 So they will be replaced with new 3 blocks.
			 *                 Final array will look like
			 *                 [old_b1, old_b2, old_b3, -- new_b4, new_b5, new_b6 --, old_b6, old_b7 )]
			 */
			let startIndex = -1,
				endIndex = -1;
			for (let index = 0; index < updatedProactiveAIBlock.length; index++) {
				const block = updatedProactiveAIBlock[index];
				// If step range is before first block then it's new blocks at the beginning of the document.
				// We know that exact placement of new blocks, so breaking loop here.
				if (index === 0 && step.from < block.from && step.to < block.to) {
					startIndex = 0;
					endIndex = 0;
					break;
				}

				/**
				 * When step changes are across multiple nodes;
				 * we will find startIndex and endIndex here.
				 */
				if (
					(block.from <= step.from && block.to >= step.from) ||
					(block.from <= step.to && block.to >= step.to)
				) {
					if (startIndex === -1) {
						startIndex = index;
						endIndex = index + 1;
					} else if (startIndex > -1) {
						endIndex = index + 1;
					}
				}

				/**
				 * or if block is within step range.
				 * but when step changes are across multiple blocks, this condition will be
				 * 	true for all the middle blocks except first and last one.
				 * So can't break here.
				 */
				if (block.from >= step.from && block.to <= step.to) {
					if (startIndex === -1) {
						startIndex = index;
						endIndex = index + 1;
					} else if (startIndex > -1) {
						endIndex = index + 1;
					}
				}

				// if range is between current and next block,
				//	thus neigher current or next block should be replaced.
				// We know that exact placement of new blocks, so breaking loop here.
				if (index < updatedProactiveAIBlock.length - 1) {
					const nextBlock = updatedProactiveAIBlock[index + 1];
					if (step.from > block.to && step.to < nextBlock.from) {
						/**
						 * We want to add new block after current block.
						 * So startIndex has to be "index + 1".
						 * Also we don't want to delete any block.
						 * So expression "endIndex - startIndex" must be 0.
						 * So setting endIndex to "index + 1".
						 */
						startIndex = index + 1;
						endIndex = index + 1;
						break;
					}
				}

				// if step range is outside last block then it's new blocks
				// This is anyway last block, so no need to break;
				if (
					index === updatedProactiveAIBlock.length - 1 &&
					step.from > block.to &&
					step.to > block.to
				) {
					startIndex = index + 1;
					endIndex = index + 1;
				}
			}

			if (startIndex > -1 && endIndex > -1) {
				updatedProactiveAIBlock.splice(startIndex, endIndex - startIndex, ...newProactiveAIBlocks);
			}

			/**
			 * Lastly update positions of blocks not affected by step with step's mapping.
			 * So that when we apply next step we have updated positions.
			 */
			updatedProactiveAIBlock = updatedProactiveAIBlock.map((block) => {
				if (block.newBlock) {
					delete block.newBlock;
					delete block.node;
				} else {
					block = updateBlockPositions(mapping, block);
				}
				return block;
			}) as ProactiveAIBlock[];
		});

		return {
			originProactiveAIBlock: proactiveAIBlocks,
			updatedProactiveAIBlock,
		};
	}
	return {
		originProactiveAIBlock: proactiveAIBlocks,
		updatedProactiveAIBlock: proactiveAIBlocks,
	};
}

export function markBlocksNotNeedingSGCheck(
	allBlocks: ProactiveAIBlock[],
	blocksToMark: ProactiveAIBlock[],
) {
	const blockIdsToMark = blocksToMark.map((block) => block.id);
	return allBlocks.map((block) => {
		if (blockIdsToMark.includes(block.id)) {
			block.needSpellingAndGrammarCheck = false;
			block.sentences = block.sentences?.map((sentence) => {
				sentence.needSpellingAndGrammarCheck = false;
				return sentence;
			});
		}
		return block;
	});
}
